import React from "react";
import PropTypes from "prop-types";
import { useFormContext, Controller } from "react-hook-form";
import EditorFormSection from "../../EditorFormSection";
import {
  Row,
  Col,
  UncontrolledPopover,
  PopoverHeader,
  PopoverBody,
} from "reactstrap";
import checkMarkSVG from "../../../assets/images/icons/checkmark.svg";
import cuttingContour from "../../../assets/images/icons/cutting-contour.svg";
import cuttingRectangular from "../../../assets/images/icons/cutting-rectangular.svg";
import cuttingExtempted from "../../../assets/images/icons/cutting-extempted.svg";
import { FaExclamationTriangle } from "react-icons/fa";

const vectorTypes = ["svg", "ai", "eps", "pdf"];

const CuttingSection = (
  { isOpen, togglePanel, cuttings, file, vectorizeFile },
  context
) => {
  const { control } = useFormContext();

  const renderCuttingTitle = (key) => {
    switch (key) {
      case "rectangular":
        return context.t("Cutting.Rectangular.Description");
      case "outline":
        return context.t("Cutting.Outline.Description");
      case "exempted":
        return context.t("Cutting.Exempted.Description");
      default:
        return key;
    }
  };

  const renderCuttingImg = (key) => {
    switch (key) {
      case "rectangular":
        return cuttingRectangular;
      case "outline":
        return cuttingContour;
      case "exempted":
        return cuttingExtempted;
      default:
        return "https://cdn.myfolie.de/media/20x20.png";
    }
  };

  const renderFileTypeWarning = (key) => {
    if (
      key !== "rectangular" &&
      file?.uploadedFile?.filetype &&
      !vectorTypes.includes(file.uploadedFile.filetype)
    ) {
      return (
        <FaExclamationTriangle className="text-warning" aria-hidden="true" />
      );
    }
    return null;
  };

  const renderPopoverContent = (key) => {
    switch (key) {
      case "rectangular":
        return context.t("Cutting.Rectangular.Popover");
      case "outline":
        return context.t("Cutting.Outline.Popover");
      case "exempted":
        return context.t("Cutting.Exempted.Popover");
      default:
        return "";
    }
  };

  return (
    <EditorFormSection
      help={context.t("FileEditor.cutting.help")}
      panelName="cutting"
      panelTitle={context.t("How should we cut your design?")}
      open={isOpen}
      toggle={togglePanel}
    >
      <Controller
        name="cutting"
        control={control}
        render={({ field: { value, onChange } }) => (
          <div>
            {cuttings.map((availableCutting, index) => {
              const isChecked = availableCutting.key === value;
              return (
                <div key={`cutting_${index}`}>
                  <Row
                    className={`cutting-field-label${
                      isChecked ? " checked" : ""
                    }`}
                    onClick={() => onChange(availableCutting.key)}
                    id={`cutting_${index}_id`}
                  >
                    <Col xs={4} className="cutting-field-icon-container">
                      <img
                        src={renderCuttingImg(availableCutting.key)}
                        className="cutting-field-icon"
                        alt="cutting-field-icon"
                        loading="lazy"
                      />
                      {isChecked && (
                        <img
                          src={checkMarkSVG}
                          className="cutting-field-checkmark"
                          alt="selected cutting"
                          loading="lazy"
                        />
                      )}
                    </Col>
                    <Col xs={8} className="cutting-field-details">
                      <div className="cutting-field-details-title">
                        <strong>
                          {context.t(availableCutting.key)}{" "}
                          {renderFileTypeWarning(availableCutting.key)}
                        </strong>
                      </div>
                      <div className="cutting-field-details-subtitle">
                        <small>
                          {renderCuttingTitle(availableCutting.key)}
                        </small>
                        <span
                          className="text-remark"
                          id={`cutting_${index}_id_details`}
                        >
                          {context.t("Details")}
                        </span>
                      </div>
                    </Col>
                  </Row>
                  <UncontrolledPopover
                    trigger="hover"
                    delay={{ show: 0, hide: 0 }}
                    placement="right"
                    target={`cutting_${index}_id_details`}
                  >
                    <PopoverHeader>
                      {context.t(availableCutting.key)}
                    </PopoverHeader>
                    <PopoverBody>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: renderPopoverContent(availableCutting.key),
                        }}
                      />
                    </PopoverBody>
                  </UncontrolledPopover>
                </div>
              );
            })}
          </div>
        )}
      />
    </EditorFormSection>
  );
};

CuttingSection.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  togglePanel: PropTypes.func.isRequired,
  cuttings: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
    })
  ).isRequired,
  file: PropTypes.object.isRequired,
  vectorizeFile: PropTypes.bool.isRequired,
};

CuttingSection.contextTypes = {
  t: PropTypes.func.isRequired,
};

export default CuttingSection;
